import { useCallback, useMemo, useState } from 'react'
import { BookMarked, Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import {
  fetchBanReasons,
  createBanReason,
  deleteBanReason,
  BAN_STR as S,
  type ReasonRow,
  type RestrictionAction,
} from './strikesApi'

const ACTIONS: { value: RestrictionAction; label: string }[] = [
  { value: 'warn', label: S.actWarn },
  { value: 'strike', label: S.actStrike },
  { value: 'mute', label: S.actMute },
  { value: 'ban', label: S.actBan },
]

const inputCls =
  'rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-border-strong'

/**
 * A8.15 — Ban sabablari katalogi. JONLI `/admin/ban-reasons` (000245):
 * standartlashtirilgan sabab-kodlar (code/category/label/default_action) —
 * inline qo'shish (code bo'yicha idempotent upsert) + o'chirish.
 */
export function BanReasonsCatalogScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const [code, setCode] = useState('')
  const [category, setCategory] = useState('')
  const [label, setLabel] = useState('')
  const [defaultAction, setDefaultAction] = useState<RestrictionAction>('strike')
  const [requiresEvidence, setRequiresEvidence] = useState(false)
  const [busy, setBusy] = useState(false)

  const add = async () => {
    if (!code.trim()) return
    setBusy(true)
    try {
      await createBanReason({
        code: code.trim(),
        category: category.trim(),
        label: label.trim(),
        default_action: defaultAction,
        requires_evidence: requiresEvidence,
      })
      toast(S.addOk, { tone: 'success' })
      setCode('')
      setCategory('')
      setLabel('')
      setReloadKey((k) => k + 1)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const columns = useMemo<AdminColumn<ReasonRow>[]>(
    () => [
      { key: 'code', header: S.code, type: 'text', width: 160 },
      { key: 'category', header: S.category, type: 'text', width: 160 },
      { key: 'label', header: S.label, type: 'text', minWidth: 200 },
      {
        key: 'default_action',
        header: S.defaultAction,
        type: 'enum',
        width: 140,
        options: [
          { value: 'warn', label: S.actWarn, status: 'review' },
          { value: 'strike', label: S.actStrike, status: 'pending' },
          { value: 'mute', label: S.actMute, status: 'review' },
          { value: 'ban', label: S.actBan, status: 'banned' },
        ],
      },
      { key: 'requires_evidence', header: S.requiresEvidence, type: 'bool', width: 130 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<ReasonRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await deleteBanReason(r.id)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchBanReasons, [])
  return (
    <>
      <PageHeader title={S.reasons} screenId="A8.15" description={S.reasonsNote} />
      <div className="flex flex-wrap items-end gap-2 p-4 pb-0">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={S.code}
          className={`${inputCls} w-40 font-mono`}
        />
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder={S.category}
          className={`${inputCls} w-40`}
        />
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={S.label}
          className={`${inputCls} min-w-[180px] flex-1`}
        />
        <select
          value={defaultAction}
          onChange={(e) => setDefaultAction(e.target.value as RestrictionAction)}
          className={inputCls}
        >
          {ACTIONS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 px-1 text-sm text-secondary">
          <input
            type="checkbox"
            checked={requiresEvidence}
            onChange={(e) => setRequiresEvidence(e.target.checked)}
          />
          {S.requiresEvidence}
        </label>
        <button
          type="button"
          onClick={add}
          disabled={busy || !code.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          <Plus size={14} />
          {S.add}
        </button>
      </div>
      <DataTable<ReasonRow>
        key={reloadKey}
        screenId="A8.15"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <BookMarked size={28} strokeWidth={1.5} />, title: S.reasons }}
      />
    </>
  )
}
